import { Link } from "react-router-dom";
import { Globe, ShieldCheck, Target, Users, ArrowRight } from "lucide-react";
import SEO from "@/components/site/SEO";

const LIFESTYLE =
  "https://images.unsplash.com/photo-1758525865438-385dbc8caa5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwZmFtaWx5JTIwdmlkZW8lMjBjYWxsfGVufDB8fHx8MTc4MTY0NDQ3MHww&ixlib=rb-4.1.0&q=85";

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us"
        description="OneDial Africa is a telecom and fintech super-app developed by Scitygate Telecom UK Ltd, combining calling, eSIM, wallet, and money transfer for the African diaspora."
        path="/about-us"
      />

      {/* HERO */}
      <section className="bg-brand-ink text-white">
        <div className="container-page py-20 lg:py-28">
          <p className="label-eyebrow text-brand-green">About OneDial Africa</p>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl max-w-3xl text-balance">
            A telecom + fintech super-app built for the African diaspora.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/75 leading-relaxed">
            OneDial Africa is developed by <strong>Scitygate Telecom UK Ltd</strong>,
            a United Kingdom registered telecommunications company headquartered in
            Manchester. Our platform brings together international calling, messaging,
            video, eSIM, airtime, data, wallet, transfers and bill payments under one roof.
          </p>
        </div>
      </section>

      {/* MISSION + IMAGE */}
      <section className="container-page py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <p className="label-eyebrow">Our mission</p>
          <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl text-brand-ink">
            Affordable communication and financial services for Africans worldwide.
          </h2>
          <p className="mt-5 text-brand-muted leading-relaxed">
            Millions of Africans live, work and study outside the continent — yet staying
            in touch and supporting family back home is still complex and expensive.
            We exist to change that. OneDial Africa replaces a dozen apps with a single,
            secure platform that respects your time and your money.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            {[
              "Per-second billing with full price transparency",
              "Bank-grade security and UK regulatory standards",
              "Coverage across 50+ African destinations",
              "Built for low-bandwidth networks and real-world use",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3" data-testid={`mission-point-${t.slice(0,18)}`}>
                <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-brand-green" />
                <span className="text-brand-ink">{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <img
            src={LIFESTYLE}
            alt="African family video calling using OneDial Africa"
            className="w-full rounded-2xl border border-brand-line shadow-lg object-cover h-[460px]"
          />
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-white border-y border-brand-line">
        <div className="container-page py-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, title: "Trust", desc: "UK-licensed operator. Encrypted by default." },
            { icon: Globe, title: "Reach", desc: "50+ African destinations and growing." },
            { icon: Target, title: "Focus", desc: "Purpose-built for the African diaspora." },
            { icon: Users, title: "People", desc: "Designed with families, students and SMEs in mind." },
          ].map((v) => (
            <div key={v.title} data-testid={`value-${v.title.toLowerCase()}`} className="rounded-xl border border-brand-line p-6">
              <v.icon className="h-6 w-6 text-brand-green" />
              <h3 className="mt-4 font-display font-bold text-xl text-brand-ink">{v.title}</h3>
              <p className="mt-2 text-sm text-brand-muted">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COMPANY CARD */}
      <section className="container-page py-24">
        <div className="rounded-2xl bg-brand-ink text-white p-10 lg:p-14 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="label-eyebrow text-brand-green">The company behind OneDial Africa</p>
            <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl">
              Scitygate Telecom UK Ltd
            </h2>
            <p className="mt-4 text-white/75 max-w-xl">
              A UK telecommunications company operating from Manchester, England.
              We invest in carrier-grade infrastructure and partnerships across Africa
              to deliver reliable voice, data and financial services.
            </p>
          </div>
          <div className="lg:col-span-5">
            <address className="not-italic text-sm text-white/85 space-y-2">
              <div className="text-xs uppercase tracking-[0.22em] text-white/55">Head Office</div>
              <div>2nd Floor, Halifax House</div>
              <div>93–101 Bridge Street</div>
              <div>Manchester, M3 2GX</div>
              <div>United Kingdom</div>
              <div className="pt-3"><a className="text-brand-green hover:underline" href="tel:+441619894842">0161 989 4842</a></div>
              <div><a className="text-brand-green hover:underline" href="mailto:support@onedialafrica.com">support@onedialafrica.com</a></div>
            </address>
            <Link
              to="/contact-us"
              data-testid="about-cta-contact"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-green px-5 py-3 text-sm font-semibold text-white hover:bg-brand-greenHover transition-colors"
            >
              Talk to us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
